import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowBack,
  Search,
  Visibility,
  Edit,
  Refresh,
  Error,
  ExpandMore,
  Clear,
} from "@mui/icons-material";
import { processedDocuments, loadMoreProcessedDocuments, searchProcessedDocuments } from "../redux/slices/LoadAIAgentsSlice";
import { getImages, getProfileImageWithStyle } from "../utills/lib";

const AllProcessedDocuments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slugName, profilePic, receviedDatafromProps } = location.state || {};
  const [visibleCount, setVisibleCount] = useState(10);

  const { processedDocumentsList, processedDocumentsListLoading, processedDocumentsPagination, processedDocumentsListError } = useSelector(
    (state) => state.LoadAIAgentsSlice
  );

  const [searchText, setSearchText] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (slugName) {
      dispatch(processedDocuments(slugName));
    }
  }, [dispatch, slugName]);

  const handleLoadMore = async () => {
    setVisibleCount((prev) => prev + 10);
    if (loadingMore || !processedDocumentsPagination.next) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    try {
      await dispatch(loadMoreProcessedDocuments({ slugname: slugName, page: nextPage })).unwrap();
    } catch (error) {
      console.error('Error loading more documents:', error);
      // Revert the page number if there was an error
      setCurrentPage(currentPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "processed":
      case "success":
        return "#2AD899";
      case "rejected":
        return "#C62828";
      case "pending":
        return "#F9A825";
      default:
        return "#757575";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "processed":
      case "success":
        return "Processed";
      case "rejected":
        return "Rejected";
      case "pending":
        return "Pending";
      default:
        return status;
    }
  };

  const filteredData = processedDocumentsList.filter((row) => {
    const matchesStatus =
      statusFilter === "all" || row.status === statusFilter;
    return matchesStatus;
  });

  const overallCount = processedDocumentsPagination?.count || 0;
  const totalDocs = processedDocumentsList.length;
  const processedDocs = processedDocumentsList.filter(doc => doc.status === "processed" || doc.status === "success").length;
  const rejectedDocs = processedDocumentsList.filter(doc => doc.status === "rejected").length;
  const pendingDocs = processedDocumentsList.filter(doc => doc.status === "pending").length;

  const handleClearSearch = () => {
    setSearchText("");

    if (isSearchMode) {
      setIsSearchMode(false);
      dispatch(processedDocuments(slugName));
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      p: { xs: 2, md: 4 }
    }}>
      <Box sx={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header */}
        <Card sx={{
          mb: 4,
          borderRadius: 4,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)"
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "white",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography variant="h3" sx={{
                    fontWeight: "bold",
                    mb: 1,
                    fontSize: { xs: "1.8rem", md: "2.5rem" }
                  }}>
                    All Processed Documents
                  </Typography>
                  <Typography variant="h6" sx={{
                    opacity: 0.9,
                    fontSize: { xs: "1rem", md: "1.2rem" }
                  }}>
                    📊 Comprehensive overview for {slugName}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body1" sx={{
                  opacity: 0.9,
                  fontSize: { xs: "0.9rem", md: "1rem" }
                }}>
                  Showing {processedDocumentsList.length} of {processedDocumentsPagination.count} documents
                </Typography>
                {processedDocumentsPagination.count > 0 && (
                  <Chip
                    label={`Page ${currentPage} of ${Math.ceil(processedDocumentsPagination.count / 10)}`}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      backdropFilter: "blur(10px)",
                    }}
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Search and Filter Section */}
        <Card sx={{
          mb: 3,
          borderRadius: 3,
          background: "white",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid rgba(0,0,0,0.05)"
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search documents..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "#6c757d" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchText ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          edge="end"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    minWidth: 300,
                    "& .MuiOutlinedInput-root": {
                      height: 42,
                      borderRadius: 3,
                      backgroundColor: "#f8f9fa",
                    },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={() => {
                    if (!slugName) return;
                    setIsSearchMode(true);
                    dispatch(
                      searchProcessedDocuments({
                        slugname: slugName,
                        query: searchText.trim(),
                      })
                    );
                  }}
                  disabled={!searchText.trim()}
                  sx={{
                    height: 42,
                    minWidth: 100,
                    borderRadius: 3,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Search
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                {["all", "processed", "rejected", "pending"].map((status) => (
                  <Chip
                    key={status}
                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                    onClick={() => setStatusFilter(status)}
                    sx={{
                      backgroundColor: statusFilter === status ? getStatusColor(status) : "#f8f9fa",
                      color: statusFilter === status ? "white" : "#495057",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: statusFilter === status ? getStatusColor(status) : "#e9ecef",
                      },
                    }}
                  />
                ))}
              </Box>

              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchText("");
                  setStatusFilter("all");
                  setCurrentPage(1);
                  setVisibleCount(10);
                  setIsSearchMode(false);
                  if (slugName) dispatch(processedDocuments(slugName));
                }}
                sx={{
                  ml: "auto",
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Refresh Data
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Enhanced Table */}
        <Card sx={{
          borderRadius: 3,
          background: "white",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid rgba(0,0,0,0.05)",
          overflow: "hidden"
        }}>
          {/* Error Display */}
          {processedDocumentsListError && (
            <Box sx={{ p: 2, backgroundColor: "#fff3cd", borderBottom: "1px solid #ffeaa7", display: "flex", alignItems: "center", gap: 2 }}>
              <Error sx={{ color: "#856404" }} />
              <Typography variant="body2" sx={{ color: "#856404" }}>
                Error loading documents: {processedDocumentsListError?.message || String(processedDocumentsListError)}
              </Typography>
            </Box>
          )}

          <TableContainer sx={{ overflowX: "hidden" }}>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow sx={{
                  background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  "& th": {
                    borderBottom: "2px solid #dee2e6",
                    fontWeight: 700,
                    color: "#495057",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 2,
                    px: 2,
                  }
                }}>
                  <TableCell>AI Agent</TableCell>
                  <TableCell>Input File</TableCell>
                  <TableCell>Output File</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedDocumentsListLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <LinearProgress sx={{ width: "100%", maxWidth: 400 }} />
                        <Typography variant="body1" sx={{ color: "#6c757d" }}>
                          Loading documents...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="h6" sx={{ color: "#6c757d", mb: 1 }}>
                        No documents found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#adb5bd" }}>
                        {searchText || statusFilter !== "all"
                          ? "Try adjusting your search or filter criteria"
                          : "No processed documents available for this agent"
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.slice(0, visibleCount).map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        },
                        "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      <TableCell sx={{ py: 2.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            src={profilePic ? getProfileImageWithStyle(profilePic).src : getImages(slugName)}
                            alt={row.ai_agent}
                            sx={{
                              width: 45,
                              height: 45,
                              border: "2px solid #e9ecef",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              ...(profilePic ? getProfileImageWithStyle(profilePic).style : {})
                            }}
                          />
                          <Box>
                            <Typography variant="body1" sx={{
                              fontWeight: 600,
                              color: "#212529"
                            }}>
                              {row.ai_agent}
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: "#6c757d",
                              fontSize: "0.75rem"
                            }}>
                              AI Agent
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                        <Box>
                          <Typography variant="body1" sx={{
                            fontWeight: 500,
                            color: "#212529",
                            mb: 0.5
                          }}>
                            {row.input_file_name}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: "#6c757d",
                            fontSize: "0.7rem"
                          }}>
                            Input Document
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                        <Box>
                          <Typography variant="body1" sx={{
                            fontWeight: 500,
                            color: "#212529",
                            mb: 0.5
                          }}>
                            {row.output_file_name || "N/A"}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: "#6c757d",
                            fontSize: "0.7rem"
                          }}>
                            Output Document
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                        <Box>
                          <Typography variant="body1" sx={{
                            fontWeight: 500,
                            color: "#212529",
                            mb: 0.5
                          }}>
                            {new Date(row.created_at).toLocaleString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                        <Chip
                          label={getStatusText(row.status)}
                          sx={{
                            backgroundColor: getStatusColor(row.status) + "20",
                            color: getStatusColor(row.status),
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            height: 28,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Button
                            variant="contained"
                            size="medium"
                            startIcon={row.status === "rejected" ? <Edit /> : <Visibility />}
                            onClick={() =>
                              navigate("/ActivityDetailsScreen", {
                                state: {
                                  row,
                                  mode: row.status === "rejected" ? "edit" : "view",
                                  receviedDatafromProps: receviedDatafromProps
                                },
                              })
                            }
                            sx={{
                              textTransform: "none",
                              background: row.status === "rejected"
                                ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                                : "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                              borderRadius: 3,
                              px: 3,
                              py: 1,
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {row.status === "rejected" ? "Edit" : "View"}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* View More Button */}
          {!isSearchMode && (
            (statusFilter === "all" && totalDocs < overallCount) ||
            (statusFilter === "processed" && processedDocs >= 10 && totalDocs < overallCount) ||
            (statusFilter === "rejected" && rejectedDocs >= 10 && totalDocs < overallCount) ||
            (statusFilter === "pending" && pendingDocs >= 10 && totalDocs < overallCount)
          ) && (
              <Box
                sx={{
                  p: 3,
                  borderTop: "1px solid #e9ecef",
                  background: "#f8f9fa",
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleLoadMore}
                  disabled={processedDocumentsListLoading || loadingMore}
                  startIcon={
                    processedDocumentsListLoading || loadingMore ? (
                      <LinearProgress size={20} />
                    ) : (
                      <ExpandMore />
                    )
                  }
                  sx={{
                    background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                    color: "white",
                    textTransform: "none",
                    borderRadius: "25px",
                    px: 4,
                    py: 1.5,
                    fontWeight: "bold",
                    fontSize: "14px",
                    boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                      boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                      transform: "translateY(-2px)"
                    },
                    "&:disabled": {
                      background: "#6c757d",
                      transform: "none",
                      boxShadow: "none"
                    },
                    transition: "all 0.3s ease",
                    fontFamily: "'Poppins', sans-serif"
                  }}
                >
                  {processedDocumentsListLoading || loadingMore ? "Loading..." : "View More"}
                </Button>
              </Box>
            )}

        </Card>
      </Box>
    </Box>
  );
};

export default AllProcessedDocuments;
